
import { Timestamp } from 'firebase/firestore';

export interface FirebaseTimestamp {
  seconds: number;
  nanoseconds: number;
}

export interface Agent {
  uid: string;
  email: string;
  companyName: string;
  contactPerson: string;
  phone: string;
  isApproved: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface Availability {
  from: Timestamp;
  to: Timestamp;
  minRentalPeriod: string; // e.g., "6 maanden", "1 jaar", "Onbepaald"
  notes?: string;
}

export enum PlaceType {
  Room = 'Kamer',
  Studio = 'Studio',
  Apartment = 'Appartement',
  House = 'Huis'
}

export interface AgeRange {
  min: number;
  max: number;
}

export interface Preferences {
  ageRange: AgeRange;
  gender: string; // e.g., "Man", "Vrouw", "Niet belangrijk"
  occupation: string[]; // e.g., ["Student", "Werkend"]
}

export interface RoommatesInfo {
  count: number;
  description: string;
  types: string[]; // e.g., ["Studenten", "Werkenden"]
  ageRange: AgeRange;
}

export interface ListingDetails {
  amenities: string[];
  availability: Availability;
  houseRules: string[];
  location: {
    address: string;
    coordinates: Coordinates; // For simplicity, assume address is primary, coords might be derived
  };
  placeType: PlaceType;
  preferences: Preferences;
  price: number; // Monthly price
  roomImages: string[]; // URLs
  roommates: RoommatesInfo;
  squareMeters: number;
}

export interface UserListingData {
  uid?: string; // UID of the users document itself, generated by Firestore
  agentUid: string; // UID of the agent who created this listing
  userType: "LISTING";
  email: string; // Agent's email
  firstName: string; // Agent's contact first name
  lastName: string; // Agent's contact last name
  age?: number; // Agent's contact age (optional)
  gender?: string; // Agent's contact gender (optional)
  description: string; // Property description
  listing: ListingDetails;
  profileImages: string[]; // Agent/Company profile images (URLs)
  onboardingCompleted: true;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Like {
  id: string; // Firestore document ID of the like
  listingId: string; // ID of the UserListingData document
  userId: string; // UID of the searching user who liked
  userName?: string; // Denormalized name of the searching user (from their profile)
  userProfileImage?: string; // Denormalized profile image URL of the searching user
  likedAt: Timestamp;
}

export interface ChatMessage {
  id?: string; // Firestore document ID
  message: string;
  senderId: string;
  timestamp: Timestamp;
  type: "text" | "image";
  imageUrl?: string;
  fileName?: string;
}

export interface Chat {
  id?: string; // Firestore document ID
  participants: string[]; // [agentUid, searchingUserUid]
  listingId: string; // ID of the UserListingData document this chat is about
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastMessage?: ChatMessage; // Optional: for displaying latest message snippet
  // Messages are stored in a subcollection "messages"
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface CurrentUser {
  uid: string;
  email: string | null;
}

export type KeysOfNonArrayObjectProperties<T> = {
  [K in keyof T]: T[K] extends object ? (T[K] extends readonly any[] ? never : K) : never;
}[keyof T];

// --- Dashboard ---

/**
 * Eén reactie op een woning, al platgeslagen voor de UI. `hasChat` en
 * `awaitingReply` komen uit het match-document zelf (`lastMessage` /
 * `lastMessageSenderId`) — daar is geen extra Firestore-verzoek voor nodig.
 */
export interface DashboardMatch {
  id: string;
  listingId: string;
  otherUserId: string;
  name: string;
  profileImage: string | null;
  lastMessage: string | null;
  lastMessageAt: Date | null;
  createdAt: Date | null;
  /** Er is minstens één bericht gewisseld. */
  hasChat: boolean;
  /** De zoeker stuurde het laatste bericht: er wordt op de makelaar gewacht. */
  awaitingReply: boolean;
}

export interface DashboardListing {
  listing: UserListingData;
  matches: DashboardMatch[];
}

export interface DashboardStats {
  matches: number;
  conversations: number;
  awaitingReply: number;
  newThisWeek: number;
  /** Langst wachtende reactie in dagen, of null als er niets wacht. */
  longestWaitDays: number | null;
}
